import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError, resolveMedia } from "@/lib/api";
import { toast } from "sonner";
import { Crosshair, LockSimple, LockSimpleOpen, ArrowsClockwise, Plus, X, Star, FloppyDisk, Trophy } from "@phosphor-icons/react";
import { Header, Modal, Sel, DivisionTabs } from "./shared";
import { ShootoutScoring, ShootoutFinal } from "./ShootoutScoring";

const VIEWS = [
  { key: "candidates", label: "Candidates" },
  { key: "scoring", label: "Live Scoring" },
];

const STATUSES = [
  { key: "candidate", label: "Candidate" },
  { key: "selected", label: "Selected" },
  { key: "not_selected", label: "Not Selected" },
];

export default function ShootoutTab() {
  const [data, setData] = useState(null);
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [division, setDivision] = useState("Open");
  const [busy, setBusy] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [view, setView] = useState("candidates");

  const load = useCallback(() => {
    api.get("/shootout").then((r) => setData(r.data)).catch(() => {});
    api.get("/players").then((r) => setPlayers(r.data)).catch(() => {});
    api.get("/teams").then((r) => setTeams(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, okMsg) => {
    setBusy(true);
    try {
      const r = await fn();
      setData(r.data);
      if (okMsg) toast.success(okMsg);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const setStatus = (pid, status) => run(() => api.put(`/shootout/candidate/${pid}`, { status }));
  const saveNotes = (pid, notes) => run(() => api.put(`/shootout/candidate/${pid}`, { notes }), "Notes saved");
  const remove = (pid, name) => {
    if (!window.confirm(`Remove ${name} from the shootout candidates? They won't auto-qualify again unless you re-add them.`)) return;
    run(() => api.delete(`/shootout/candidate/${pid}`), "Candidate removed");
  };
  const lock = () => {
    if (!window.confirm(`Lock the final shootout participants across all divisions? Only the ${data.selected_count} Selected player(s) will show publicly, and the list stops auto-updating.`)) return;
    run(() => api.post("/shootout/lock"), "Participants locked");
  };
  const unlock = () => run(() => api.post("/shootout/unlock"), "Unlocked — list is live again");
  const reset = () => {
    if (!window.confirm("Reset all overrides? Manual additions, removals, statuses and notes will be cleared, returning to the automatic top-2 per team.")) return;
    run(() => api.post("/shootout/reset"), "Reset to automatic qualifiers");
  };
  const score = (pid, points, round = "prelim") => run(() => api.put(`/shootout/result/${pid}`, { points, round }), `Score saved — ${points} pts`);
  const resetScore = (pid, name, round = "prelim") => {
    if (!window.confirm(`Reset ${name}'s ${round === "final" ? "final round" : round === "tiebreaker" ? "tiebreaker" : "shootout"} score? They will go back to Waiting.`)) return;
    run(() => api.delete(`/shootout/result/${pid}?round=${round}`), "Score reset");
  };
  const clearScores = () => {
    if (!window.confirm(`Clear every recorded ${division} shootout score (including the final round)? This can't be undone.`)) return;
    run(() => api.post(`/shootout/results/clear?division=${encodeURIComponent(division)}`), `${division} scores cleared`);
  };
  const startFinal = () => {
    if (!window.confirm(`Start the Final Round? The top ${data.finalists_count} preliminary scorers across ALL divisions advance to shoot for one league champion. The finalist list is fixed once started.`)) return;
    run(() => api.post("/shootout/final/start"), "Final round started");
  };
  const cancelFinal = () => {
    if (!window.confirm("Cancel the Final Round? All final-round scores will be removed.")) return;
    run(() => api.delete("/shootout/final"), "Final round cancelled");
  };
  const startTiebreaker = () => run(() => api.post("/shootout/final/tiebreaker/start"), "Sudden-death tiebreaker started");
  const cancelTiebreaker = () => {
    if (!window.confirm("Cancel this tiebreaker round? Its scores will be removed.")) return;
    run(() => api.delete("/shootout/final/tiebreaker"), "Tiebreaker round cancelled");
  };

  if (!data) return <p className="text-white/40">Loading…</p>;

  const cands = (data.candidates || []).filter((c) => c.division === division || !c.division);
  const byTeam = [];
  const idx = {};
  cands.forEach((c) => {
    if (idx[c.team_id] == null) { idx[c.team_id] = byTeam.length; byTeam.push({ ...c, list: [] }); }
    byTeam[idx[c.team_id]].list.push(c);
  });
  const candidateIds = new Set((data.candidates || []).map((c) => c.player_id));
  const divisionTeamIds = new Set(teams.filter((t) => t.division === division).map((t) => t.id));
  const addablePlayers = players.filter((p) => !candidateIds.has(p.id) && (divisionTeamIds.has(p.team_id) || !p.team_id));

  return (
    <div data-testid="shootout-tab" className="max-w-5xl">
      <Header title="3PT Shootout" action={(
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {view === "candidates" && !data.locked && (
            <>
              <button onClick={reset} disabled={busy} data-testid="shootout-reset-btn" className="flex items-center gap-2 border border-white/15 text-white/60 hover:text-white font-bold uppercase tracking-wide px-4 py-2.5 text-sm transition-all duration-200">
                <ArrowsClockwise size={15} /> Reset to Auto
              </button>
              <button onClick={() => setShowAdd(true)} disabled={busy} data-testid="shootout-add-btn" className="flex items-center gap-2 border border-[#FF5722]/50 bg-[#FF5722]/10 text-[#FF5722] hover:bg-[#FF5722]/20 font-bold uppercase tracking-wide px-4 py-2.5 text-sm transition-all duration-200">
                <Plus size={15} weight="bold" /> Add Candidate
              </button>
            </>
          )}
          {view === "candidates" && (data.locked ? (
            <button onClick={unlock} disabled={busy} data-testid="shootout-unlock-btn" className="flex items-center gap-2 border border-[#00E676]/50 text-[#00E676] hover:bg-[#00E676]/10 font-bold uppercase tracking-wide px-4 py-2.5 text-sm transition-all duration-200">
              <LockSimpleOpen size={15} weight="fill" /> Unlock
            </button>
          ) : (
            <button onClick={lock} disabled={busy || data.selected_count === 0} data-testid="shootout-lock-btn" title={data.selected_count === 0 ? "Mark at least one candidate as Selected first" : ""}
              className="flex items-center gap-2 bg-[#FF5722] hover:bg-[#FF7043] disabled:opacity-40 text-black font-bold uppercase tracking-wide px-4 py-2.5 text-sm transition-all duration-200">
              <LockSimple size={15} weight="fill" /> Lock Participants ({data.selected_count})
            </button>
          ))}
        </div>
      )} />
      <div className="flex items-center gap-1 border border-white/10 bg-[#121212] p-1 w-fit -mt-2 mb-5" data-testid="shootout-view-toggle">
        {VIEWS.map((v) => (
          <button key={v.key} onClick={() => setView(v.key)} data-testid={`shootout-view-${v.key}`}
            className={`px-4 py-2 text-xs font-bold uppercase tracking-wide transition-all duration-200 ${view === v.key ? "bg-[#FF5722] text-black" : "text-white/60 hover:text-white"}`}>
            {v.key === "scoring" && <Trophy size={13} weight="fill" className="inline mr-1.5 -mt-0.5" />}{v.label}
          </button>
        ))}
      </div>
      <p className="text-white/50 text-sm mb-6">
        {view === "scoring"
          ? <>Enter each player's total after their turn — the leaderboard re-ranks by highest score and updates the public page live. Once preliminaries are done, start the <span className="text-[#FFEA00] font-semibold">Final Round</span>: the top {data.finalists_count || 8} shooters across all divisions advance and the highest final score is crowned the one league champion.</>
          : <>The top 2 players on each team by <span className="text-[#FF5722] font-semibold">3-pointers made</span> (from Scorekeeper box scores) qualify automatically and update as games are finalized. Mark your final picks as <span className="text-[#FF5722] font-semibold">Selected</span>, then lock the field.</>}
      </p>

      {view === "scoring" && (
        <ShootoutFinal allCands={data.candidates || []} final={data.final} finalistsCount={data.finalists_count || 8} busy={busy}
          onScore={score} onReset={resetScore} onStart={startFinal} onCancel={cancelFinal}
          onStartTiebreaker={startTiebreaker} onCancelTiebreaker={cancelTiebreaker} />
      )}

      <DivisionTabs value={division} onChange={setDivision} prefix="shootout" />

      {view === "scoring" ? (
        <ShootoutScoring cands={cands} division={division} final={data.final} busy={busy}
          onScore={score} onReset={resetScore} onClearAll={clearScores} />
      ) : (<>
      {data.locked && (
        <div className="flex items-center gap-2 border border-[#FF5722] bg-[#FF5722]/10 text-[#FF5722] px-4 py-3 mb-6 text-sm font-bold uppercase tracking-wide" data-testid="shootout-admin-locked-banner">
          <LockSimple size={16} weight="fill" /> Participants locked — the public page shows only Selected players. Unlock to make changes.
        </div>
      )}

      {cands.length === 0 ? (
        <div className="border-2 border-dashed border-white/15 py-16 text-center" data-testid="shootout-admin-empty">
          <Crosshair size={40} className="text-white/25 mx-auto mb-4" />
          <p className="text-white/50">No {division} division candidates yet. Qualifiers appear automatically once players record 3-pointers in scored games — or add someone manually.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {byTeam.map((t) => (
            <section key={t.team_id || t.team_name} className="border border-white/10 bg-[#121212]" data-testid={`shootout-admin-team-${t.team_id}`}>
              <div className="flex items-center gap-3 px-5 py-3 border-b border-white/10">
                {t.team_logo
                  ? <img src={resolveMedia(t.team_logo)} alt="" className="w-6 h-6 object-contain" />
                  : <span className="w-6 h-6 flex items-center justify-center font-display text-xs" style={{ backgroundColor: t.team_color || "#FF5722", color: "#000" }}>{(t.team_name || "?").charAt(0)}</span>}
                <h3 className="font-display text-xl uppercase">{t.team_name}</h3>
                {t.division && <span className="text-white/40 text-xs uppercase tracking-wide">{t.division}</span>}
              </div>
              <div className="divide-y divide-white/5">
                {t.list.map((c) => (
                  <CandidateRow key={c.player_id} c={c} locked={data.locked} busy={busy}
                    onStatus={setStatus} onNotes={saveNotes} onRemove={remove} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
      </>)}

      {showAdd && (
        <AddCandidateModal players={addablePlayers} division={division}
          onClose={() => setShowAdd(false)}
          onAdd={(pid) => { setShowAdd(false); run(() => api.post("/shootout/candidates", { player_id: pid }), "Candidate added"); }} />
      )}
    </div>
  );
}

function CandidateRow({ c, locked, busy, onStatus, onNotes, onRemove }) {
  const [notes, setNotes] = useState(c.notes || "");
  useEffect(() => { setNotes(c.notes || ""); }, [c.notes]);
  const dirty = notes !== (c.notes || "");

  return (
    <div className="px-5 py-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`shootout-row-${c.player_id}`}>
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {c.photo_url
          ? <img src={resolveMedia(c.photo_url)} alt="" className="w-10 h-10 object-cover shrink-0" />
          : <span className="w-10 h-10 flex items-center justify-center font-display text-lg shrink-0" style={{ backgroundColor: c.team_color || "#FF5722", color: "#000" }}>{(c.name || "?").charAt(0)}</span>}
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-display text-lg uppercase truncate leading-tight">{c.name}</p>
            {c.number != null && c.number !== "" && <span className="text-white/50 text-sm font-display">#{c.number}</span>}
            {c.rank
              ? <span className="text-[10px] uppercase tracking-wide font-bold text-[#00E676]">Auto #{c.rank}</span>
              : <span className="text-[10px] uppercase tracking-wide font-bold text-[#FFEA00]">Manual</span>}
          </div>
          <p className="text-[#FF5722] font-display text-base tabular-nums leading-tight">{c.tpm} <span className="text-white/40 text-[10px] uppercase tracking-wide font-sans font-bold">3PM</span></p>
        </div>
      </div>

      <div className="flex items-center gap-1.5 flex-wrap">
        {STATUSES.map((s) => (
          <button key={s.key} disabled={locked || busy} onClick={() => onStatus(c.player_id, s.key)}
            data-testid={`shootout-status-${s.key}-${c.player_id}`}
            className={`text-[10px] font-bold uppercase tracking-wide px-2.5 py-1.5 transition-all duration-200 disabled:opacity-40 ${
              c.status === s.key
                ? s.key === "selected" ? "bg-[#FF5722] text-black" : s.key === "not_selected" ? "bg-white/20 text-white" : "bg-[#FFEA00]/20 text-[#FFEA00] border border-[#FFEA00]/40"
                : "border border-white/15 text-white/50 hover:text-white"
            }`}>
            {s.key === "selected" && <Star size={10} weight="fill" className="inline mr-1 -mt-0.5" />}{s.label}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2 sm:w-64">
        <input value={notes} disabled={locked} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (optional)"
          data-testid={`shootout-notes-${c.player_id}`}
          className="flex-1 bg-[#1A1A1A] border border-white/10 px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-[#FF5722] disabled:opacity-40" />
        {dirty && (
          <button onClick={() => onNotes(c.player_id, notes)} disabled={busy} data-testid={`shootout-save-notes-${c.player_id}`} className="text-[#FF5722] hover:text-[#FF7043] p-1">
            <FloppyDisk size={17} weight="fill" />
          </button>
        )}
        {!locked && (
          <button onClick={() => onRemove(c.player_id, c.name)} disabled={busy} data-testid={`shootout-remove-${c.player_id}`} className="text-white/30 hover:text-[#FF3B30] p-1 transition-colors">
            <X size={16} />
          </button>
        )}
      </div>
    </div>
  );
}

function AddCandidateModal({ players, division, onClose, onAdd }) {
  const sorted = [...players].sort((a, b) => (b.threes || 0) - (a.threes || 0) || (a.name || "").localeCompare(b.name || ""));
  const [pid, setPid] = useState("");
  return (
    <Modal open onClose={onClose} title={`Add Shootout Candidate — ${division}`}>
      <div className="space-y-4" data-testid="shootout-add-modal">
        <p className="text-white/40 text-xs">Pick any existing {division} division player — no duplicate records are created. Their 3PM stays synced with the Scorekeeper stats.</p>
        <Sel label="Player" value={pid} onChange={(e) => setPid(e.target.value)} data-testid="shootout-add-select">
          <option value="">— Select player —</option>
          {sorted.map((p) => <option key={p.id} value={p.id}>{`${p.name}${p.number != null && p.number !== "" ? ` #${p.number}` : ""} — ${p.threes || 0} 3PM`}</option>)}
        </Sel>
        <button onClick={() => pid && onAdd(pid)} disabled={!pid} data-testid="shootout-add-confirm"
          className="w-full bg-[#FF5722] hover:bg-[#FF7043] disabled:opacity-40 text-black font-bold uppercase tracking-wide py-2.5 text-sm transition-all duration-200">
          Add Candidate
        </button>
      </div>
    </Modal>
  );
}
